import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import * as P from '@radix-ui/react-popover'
import { Check, ChevronsUpDown, Plus, X } from 'lucide-react'
import { cn } from './cn'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './command'
import { useFieldControl } from './field'
import { Spinner } from './spinner'
import { toast } from './toast'
import { useDebounce } from '@/lib/hooks'

export interface ComboboxOption { value: string; label: string; description?: string; icon?: ReactNode; disabled?: boolean }

interface Base {
  /** Static options (filtered client-side). */
  options?: ComboboxOption[]
  /** Async search: called (debounced 200 ms) with the query; return matching options. */
  loadOptions?: (query: string, signal: AbortSignal) => Promise<ComboboxOption[]>
  /** Labels for already-selected values that may not be in `options` (async lists). */
  selectedOptions?: ComboboxOption[]
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: ReactNode
  /** Offer "Create “query”" when nothing matches exactly. Runs once at a time (Enter twice creates
   *  one item); a thrown error is toasted and the query kept so the merchant can retry. */
  onCreate?: (query: string) => void | Promise<void>
  createLabel?: (query: string) => ReactNode
  disabled?: boolean
  invalid?: boolean
  id?: string
  className?: string
  size?: 'sm' | 'md'
  'aria-label'?: string
}
export interface ComboboxSingleProps extends Base { multiple?: false; value: string | null; onChange: (value: string | null) => void; clearable?: boolean }
export interface ComboboxMultiProps extends Base { multiple: true; value: string[]; onChange: (value: string[]) => void; max?: number }
export type ComboboxProps = ComboboxSingleProps | ComboboxMultiProps

/**
 * Searchable select, static or async, single or multi.
 * `<Combobox value={vendor} onChange={setVendor} options={vendors} onCreate={q => addVendor(q)} />`
 * `<Combobox multiple value={ids} onChange={setIds} loadOptions={(q, signal) => searchCollections(q, signal)} selectedOptions={current} />`
 */
export function Combobox(props: ComboboxProps) {
  const { options, loadOptions, selectedOptions, placeholder = 'Select…', searchPlaceholder = 'Search…', emptyText = 'No matches', onCreate, createLabel, disabled, invalid, id, className, size = 'md' } = props
  const f = useFieldControl()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const debounced = useDebounce(query, loadOptions ? 200 : 0)
  const [remote, setRemote] = useState<ComboboxOption[]>([])
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [creating, setCreating] = useState<string | null>(null)
  const creatingRef = useRef(false)
  const known = useRef(new Map<string, ComboboxOption>())
  for (const o of [...(options ?? []), ...(selectedOptions ?? []), ...remote]) known.current.set(o.value, o)

  useEffect(() => {
    if (!loadOptions || !open) return
    const ctrl = new AbortController()
    setLoading(true); setLoadError(null)
    loadOptions(debounced, ctrl.signal)
      .then(r => { if (!ctrl.signal.aborted) setRemote(Array.isArray(r) ? r : []) })
      .catch(e => { if (!ctrl.signal.aborted) setLoadError(e instanceof Error ? e.message : 'Couldn’t load options') })
      .finally(() => { if (!ctrl.signal.aborted) setLoading(false) })
    return () => ctrl.abort()
  }, [debounced, open, loadOptions])

  const list = useMemo(() => {
    if (loadOptions) return remote
    const q = query.trim().toLowerCase()
    const all = options ?? []
    if (!q) return all
    // Rank: exact label → label starts with → a word starts with → substring anywhere ('India' before 'British Indian Ocean Territory').
    const rank = (o: typeof all[number]) => {
      const l = o.label.toLowerCase(), v = o.value.toLowerCase()
      if (l === q || v === q) return 0
      if (l.startsWith(q)) return 1
      if (l.split(/[\s,/()-]+/).some(w => w.startsWith(q))) return 2
      if (l.includes(q) || v.includes(q)) return 3
      if (o.description?.toLowerCase().includes(q)) return 4
      return 9
    }
    return all.map((o, i) => ({ o, r: rank(o), i })).filter(x => x.r < 9).sort((a, b) => a.r - b.r || a.i - b.i).map(x => x.o)
  }, [loadOptions, remote, options, query])

  const selected: string[] = props.multiple ? props.value : props.value ? [props.value] : []
  const isSel = (v: string) => selected.includes(v)
  const labelOf = (v: string) => known.current.get(v)?.label ?? v
  const exact = list.some(o => o.label.toLowerCase() === query.trim().toLowerCase())
  const atMax = props.multiple && props.max !== undefined && props.value.length >= props.max

  const choose = (v: string) => {
    if (props.multiple) {
      props.onChange(isSel(v) ? props.value.filter(x => x !== v) : atMax ? props.value : [...props.value, v])
      setQuery('')
    } else {
      props.onChange(v === props.value && props.clearable ? null : v)
      setOpen(false)
    }
  }
  const create = async () => {
    const q = query.trim()
    if (!q || !onCreate || creatingRef.current) return
    creatingRef.current = true
    setCreating(q)
    try {
      await onCreate(q)
      setQuery('')
      if (!props.multiple) setOpen(false)
    } catch (e) {
      toast.error(e)
    } finally {
      creatingRef.current = false
      setCreating(null)
    }
  }
  const onTriggerKey = (e: KeyboardEvent) => {
    if (props.multiple && (e.key === 'Backspace' || e.key === 'Delete') && props.value.length && !open) props.onChange(props.value.slice(0, -1))
  }

  const isInvalid = invalid ?? f?.invalid ?? false
  return (
    <P.Root open={open} onOpenChange={o => { setOpen(o); if (!o) setQuery('') }}>
      <P.Trigger asChild disabled={disabled ?? f?.disabled}>
        <button
          type="button"
          id={id ?? f?.id}
          role="combobox"
          aria-expanded={open}
          aria-invalid={isInvalid || undefined}
          aria-describedby={f?.describedBy}
          aria-label={props['aria-label']}
          onKeyDown={onTriggerKey}
          className={cn(
            'group flex w-full min-w-0 items-center gap-2 rounded-control border border-input bg-surface px-3 text-left text-text shadow-control outline-none',
            'transition-[border-color,box-shadow] duration-150 hover:border-border-strong focus-visible:border-focus focus-visible:ring-3 focus-visible:ring-focus/20',
            'data-[state=open]:border-focus data-[state=open]:ring-3 data-[state=open]:ring-focus/20 aria-[invalid=true]:border-danger',
            'disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-text-subtle',
            props.multiple && selected.length ? 'min-h-8 flex-wrap py-1' : size === 'sm' ? 'h-7 text-sm' : 'h-8 text-base',
            className,
          )}
        >
          {selected.length === 0 && <span className="flex-1 truncate text-text-subtle">{placeholder}</span>}
          {!props.multiple && selected.length > 0 && (
            <span className="flex min-w-0 flex-1 items-center gap-2 truncate">
              {known.current.get(selected[0])?.icon}{labelOf(selected[0])}
            </span>
          )}
          {props.multiple && selected.length > 0 && (
            <span className="flex min-w-0 flex-1 flex-wrap gap-1">
              {props.value.map(v => (
                <span key={v} className="inline-flex h-6 max-w-full items-center gap-1 rounded-[6px] bg-surface-active pr-1 pl-2 text-sm text-text">
                  <span className="truncate">{labelOf(v)}</span>
                  <span role="button" tabIndex={-1} aria-label={`Remove ${labelOf(v)}`}
                    onPointerDown={e => { e.preventDefault(); e.stopPropagation(); props.onChange(props.value.filter(x => x !== v)) }}
                    className="grid size-4 place-items-center rounded text-text-subtle hover:bg-border hover:text-text"><X className="size-3" /></span>
                </span>
              ))}
            </span>
          )}
          {!props.multiple && props.clearable && props.value && !disabled && (
            <span role="button" tabIndex={-1} aria-label="Clear"
              onPointerDown={e => { e.preventDefault(); e.stopPropagation(); (props as ComboboxSingleProps).onChange(null) }}
              className="grid size-5 shrink-0 place-items-center rounded text-text-subtle hover:bg-surface-active hover:text-text"><X className="size-3.5" /></span>
          )}
          <ChevronsUpDown className="ml-auto size-4 shrink-0 self-center text-text-subtle" />
        </button>
      </P.Trigger>
      <P.Portal>
        <P.Content align="start" sideOffset={4} collisionPadding={8}
          className="z-[60] w-[var(--radix-popover-trigger-width)] min-w-60 max-w-[calc(100vw-16px)] overflow-hidden rounded-card border border-border bg-surface-raised shadow-popover data-[state=open]:animate-pop-in data-[state=closed]:animate-pop-out">
          <Command shouldFilter={false} loop>
            <CommandInput value={query} onValueChange={setQuery} placeholder={searchPlaceholder} className="h-10 text-base"
              end={loading ? <Spinner size={14} className="text-text-subtle" /> : undefined} />
            <CommandList className="max-h-72">
              {!loading && list.length === 0 && !(onCreate && query.trim()) && <CommandEmpty>{loadError ?? emptyText}</CommandEmpty>}
              <CommandGroup>
                {list.map(o => (
                  <CommandItem key={o.value} value={o.value} disabled={o.disabled || (atMax && !isSel(o.value))} onSelect={() => choose(o.value)} icon={o.icon}
                    hint={o.description} className="h-auto min-h-8 py-1"
                    end={<Check className={cn('size-4 text-primary-strong', isSel(o.value) ? 'opacity-100' : 'opacity-0')} />}>
                    {o.label}
                  </CommandItem>
                ))}
                {onCreate && query.trim() && !exact && (
                  <CommandItem value={`__create__${query}`} onSelect={() => void create()} disabled={creating !== null} aria-busy={creating !== null || undefined}
                    icon={creating !== null ? <Spinner size={14} /> : <Plus />}>
                    {creating !== null ? <>Creating “{creating}”…</> : createLabel ? createLabel(query.trim()) : <>Create “{query.trim()}”</>}
                  </CommandItem>
                )}
              </CommandGroup>
            </CommandList>
            {props.multiple && props.max !== undefined && (
              <div className="border-t border-border px-3 py-2 text-xs text-text-subtle">{props.value.length} of {props.max} selected</div>
            )}
          </Command>
        </P.Content>
      </P.Portal>
    </P.Root>
  )
}
